(ns leg.theme
  "The two palettes and which one is on, the four faces and which of them
  the newspaper is set in.

  A reader is a thing somebody looks at for half an hour at a time, so the
  page gets out of the way: paper and ink, one accent, and a line where a
  line is needed. Nothing is coloured to be noticed - what is unread is
  darker than what is read, and that is the only emphasis the list has.

  The paper is newsprint and the ink is nearly black: a warm off-white that
  does not throw a phone's backlight back at somebody reading at night, and
  a single red that a newspaper would have used for the one thing it wanted
  read first. Light is the colour of paper; dark is not \"paper inverted\" -
  nothing is that - but ink-coloured, warm, so the same page at night is
  the same page.

  Light and dark are the same layout in two sets of colours. Which one is
  used is the person's setting, and \"system\" - the default - means the
  one the machine is in, followed live: somebody whose phone turns dark at
  sunset should not have to come here and say so.

  The faces are the same question asked about type: the newspaper's
  serifs, the grotesque that the furniture is already in, or one fixed
  width for everything. All three are a setting a person made, and none of
  them is guessed from the device.

  `:sel` is the colour of a selection, and it is in here rather than left
  to the browser for the same reason the rest is: a page that themes
  everything except the one thing somebody drags a finger across has
  Chrome's blue in the middle of it."
  (:require [kc.core :as kc]))

;;; `:raised` and `:shade` are the one thing on this page that is not on
;;; the page: a menu or a dialog drawn over it. Newsprint on newsprint is
;;; invisible, so what is raised is a shade lighter than the panel in the
;;; light and a shade lighter than the panel in the dark too - away from
;;; the page in both, since "above" is the direction, not "brighter" -
;;; and `:shade` is the ink the shadow is made of, warm like the rest and
;;; not the browser's neutral grey.

(def light
  {:bg      "#f2ede1"
   :panel   "#f7f3e8"
   :sidebar "#ece5d5"
   :fg      "#1c1915"
   :dim     "#6d6453"
   :line    "#c6bba3"
   :hover   "#e6dfcd"
   :cursor  "#e2d9c4"
   :accent  "#8f2f20"
   :warn    "#a6231a"
   :sel     "#e2d0ad"
   :raised  "#fffdf6"
   :shade   "rgba(28, 25, 21, .30)"})

(def dark
  {:bg      "#17150f"
   :panel   "#1b1913"
   :sidebar "#131108"
   :fg      "#e5dcc7"
   :dim     "#9a9280"
   :line    "#3c3527"
   :hover   "#221f17"
   :cursor  "#2a2618"
   :accent  "#d98b74"
   :warn    "#ff8a72"
   :sel     "#3b3321"
   :raised  "#2e2a1b"
   :shade   "rgba(0, 0, 0, .55)"})

(defn colors [mode] (if (= "dark" mode) dark light))

;;; ------------------------------------------------------------------ the type
;;;
;;; Three faces, each with one job, which is how a newspaper page is set:
;;; a display serif for what is a headline, a text serif for what is read,
;;; and a grotesque for everything that is not the newspaper but the
;;; furniture around it - the date, the feed's name, the buttons.
;;;
;;; And a fourth that has no job on the page and takes every one of them
;;; where somebody asks for it: JetBrains Mono, the fixed face (see
;;; `faces`). It is named nowhere in this namespace, because nothing in
;;; the window has to name it - the two variables below are what every
;;; component reads, and `--leser-font-mono` is what index.html puts
;;; behind them when the attribute says so.
;;;
;;; They are served from `public/font` and nowhere else. A font loaded from
;;; Google's servers would tell Google which pages somebody reads, and an
;;; installed reader with no network would have none.

(def font-head
  "Headlines. Playfair Display, at 700-900: a high-contrast serif with the
  thin hairlines of a masthead. Unreadable as body text at this size,
  which is why it is not used for any - and the grotesque instead where
  somebody has asked for sans-serif.

  A variable and not a stack, because the stacks are in index.html: the
  article's markup is styled by its tags there and no component sees it,
  so a face named twice would be a face that is two faces (see `face`)."
  "var(--leser-font-head)")

(def font-body
  "The text of an article. Source Serif, which was cut for reading on a
  screen and keeps its stems at fifteen pixels - a feed reader shows
  somebody else's prose, and prose is a serif until somebody says
  otherwise."
  "var(--leser-font-body)")

(def font-ui
  "Everything that is not the newspaper: the toolbar, the dates, the
  counts. Libre Franklin, the American newspaper grotesque, in small
  letter-spaced capitals where a paper would have set a kicker. It is the
  one face the setting does not touch - the furniture was never a serif.

  Not the names of the feeds, though, however much a list of them looks
  like navigation: a paper's name is the paper's, so `leg.sidebar` sets it
  in `font-head` like the headlines beside it. Underneath one is the
  kicker, where a feed's name *is* furniture - there it is an attribution
  under a headline and not the thing being pointed at."
  "var(--leser-font-ui)")

(def font
  "What MUI is told, which is the furniture's face: a component that says
  nothing about its type is a button or a label, never prose."
  font-ui)

;;; -------------------------------------------------------- which face it is

(def faces
  "The three ways this page can be set, and only one of them is another
  font. \"sans\" is the furniture's grotesque used for the newspaper as
  well: a face already served, already in the worker's shell, and nothing
  more to fetch.

  \"mono\" is the one that is a font of its own: JetBrains Mono, cut for
  code and a text face by accident of being a good one, and the only
  setting on this page that costs a download. It is here for the same reason \"sans\" is -
  somebody who reads in one face all day reads better in it than in the
  one a newspaper would have picked for them - and because it is fetched
  only where it is chosen it is out of the worker's shell as well
  (`public/sw.js`). It is drawn large, as a face for code is, and is set to
  84% in its own `@font-face` so that no size in this window has to know
  which face is on - every one of them was chosen against the text serif. Where it is on, the code in an article is set in it
  too: the file is already here, and prose in one fixed face quoting code
  in another is two grids on one page."
  #{"serif" "sans" "mono"})

(defn face
  "Which of the three a setting means. Anything else is the newspaper, for
  the same reason `use-mode` treats an unknown mode as the machine's: what
  a value means is the window's business, so the server can store the word
  it was handed without knowing the list."
  [setting]
  (if (contains? faces setting) setting "serif"))

(def ^:private face-attribute "data-leser-face")

(defn set-face!
  "Say on `<html>` which of the three the page is set in.

  An attribute and not a prop, because CSS is where it is read: the
  article's body is markup the server wrote (`leg.article/Body`) and is
  styled by its tags in index.html, so a face passed down through
  components would reach everything except the prose. `leg.vt` writes the
  direction of a transition in the same place and for the same reason."
  [setting]
  (some-> js/document .-documentElement (.setAttribute face-attribute (face setting))))

;;; ------------------------------------------------------- what the machine says

(defn- media []
  (when (exists? js/matchMedia)
    (js/matchMedia "(prefers-color-scheme: dark)")))

(defonce ^:private !system
  (atom (if (some-> (media) .-matches) "dark" "light")))

(defn install!
  "Follow the machine's own setting for as long as this window is open.
  Registered once; the listener stays, because a window that is open when
  the sun goes down should change with it."
  []
  (when-let [m (media)]
    (.addEventListener m "change"
                       (fn [^js e] (reset! !system (if (.-matches e) "dark" "light"))))))

(defn use-mode
  "The palette to draw in: \"light\" or \"dark\", never \"system\". The
  setting decides, and where it says to ask the machine, the machine is
  asked here - so no component has to know that \"system\" is not a
  palette."
  [setting]
  (let [system (kc/use-atom !system)]
    (if (contains? #{"light" "dark"} setting) setting (or system "light"))))
